export interface DocumentoInstitucional {
  id?: string;
  idInstituicao: string;
  tipoDocumento: TipoDocumentoInstitucional;
  /** Nome de armazenamento; pode não ter extensão (uploads recentes são UUID puro). */
  nomeArquivo: string;
  nomeOriginal: string;
  tipoMime?: string | null;
  tamanhoBytes: number;
  dataUpload: string;
  usuarioUpload?: string;
  dataEmissao?: string;
  dataValidade?: string;
  numeroDocumento?: string;
  assinado?: boolean;
  documentCode?: string;
  statusDocumento: StatusDocumento;
  observacoes?: string;
  motivoReprovacao?: string;
}

export type TipoDocumentoInstitucional =
  // Documentos Institucionais
  | 'CARTAO_CNPJ'
  | 'ESTATUTO_SOCIAL'
  | 'ALVARA_FUNCIONAMENTO'
  | 'DECLARACAO_UTILIDADE_PUBLICA'
  | 'ATA_ELEICAO_DIRETORIA'
  | 'COMPROVANTE_INSCRICAO_CONSELHO'
  | 'COMPROVANTE_ENDERECO_INSTITUICAO'
  | 'DECLARACAO_CAPACIDADE_TECNICA'
  | 'COMPROVANTE_EXPERIENCIA'
  | 'DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES'
  | 'RELACAO_NOMINAL_DIRIGENTES'
  | 'DECLARACAO_CONDICOES_INSTALACAO'
  | 'DECLARACAO_NAO_INCORRE_IMPEDIMENTOS'
  | 'DECLARACAO_NAO_CONTRATACAO'
  // Certidões Negativas da Instituição
  | 'CERTIDAO_TRIBUTOS_FEDERAIS'
  | 'CERTIDAO_TRIBUTOS_ESTADUAIS_PGE'
  | 'CERTIFICADO_FGTS'
  | 'CERTIDAO_DEBITOS_TRABALHISTAS'
  | 'CERTIDAO_DEBITOS_MUNICIPAIS'
  | 'CERTIDAO_TCE_INSTITUICAO'
  // Documentos de Dirigente
  | 'RG_DIRIGENTE'
  | 'CPF_DIRIGENTE'
  | 'COMPROVANTE_ENDERECO_DIRIGENTE'
  | 'CERTIDAO_TCE_DIRIGENTE';

export type StatusDocumento =
  | 'PENDENTE_ENVIO'
  | 'ENVIADO'
  | 'EM_ANALISE'
  | 'APROVADO'
  | 'REPROVADO'
  | 'VENCIDO'
  | 'SUBSTITUIDO';

export const TIPOS_DOCUMENTO_LABELS: Record<TipoDocumentoInstitucional, string> = {
  // Documentos Institucionais
  CARTAO_CNPJ: 'Cartão do CNPJ',
  ESTATUTO_SOCIAL: 'Estatuto Social',
  ALVARA_FUNCIONAMENTO: 'Alvará de Funcionamento',
  DECLARACAO_UTILIDADE_PUBLICA: 'Declaração de Utilidade Pública Municipal',
  ATA_ELEICAO_DIRETORIA: 'Ata da Última Eleição de Diretoria',
  COMPROVANTE_INSCRICAO_CONSELHO: 'Comprovante de Inscrição no Conselho Municipal',
  COMPROVANTE_ENDERECO_INSTITUICAO: 'Comprovante de Endereço',
  DECLARACAO_CAPACIDADE_TECNICA: 'Declaração de capacidade técnica e gerencial',
  COMPROVANTE_EXPERIENCIA: 'Comprovante de experiência',
  DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES: 'Declaração de composição do quadro de dirigentes',
  RELACAO_NOMINAL_DIRIGENTES: 'Relação nominal dos dirigentes',
  DECLARACAO_CONDICOES_INSTALACAO: 'Declaração de condições de instalação',
  DECLARACAO_NAO_INCORRE_IMPEDIMENTOS: 'Declaração de não incorrência de impedimentos',
  DECLARACAO_NAO_CONTRATACAO: 'Declaração de não contratação de servidor público',
  // Certidões Negativas da Instituição
  CERTIDAO_TRIBUTOS_FEDERAIS: 'Certidão Negativa de Tributos Federais',
  CERTIDAO_TRIBUTOS_ESTADUAIS_PGE: 'Certidão Negativa de Tributos Estaduais – PGE',
  CERTIFICADO_FGTS: 'Certificado de Regularidade do FGTS',
  CERTIDAO_DEBITOS_TRABALHISTAS: 'Certidão Negativa de Débitos Trabalhistas',
  CERTIDAO_DEBITOS_MUNICIPAIS: 'Certidão Negativa de Débitos Municipais',
  CERTIDAO_TCE_INSTITUICAO: 'Certidão Negativa do TCE/MT - Instituição',
  // Documentos Pessoais
  RG_DIRIGENTE: 'RG',
  CPF_DIRIGENTE: 'CPF',
  COMPROVANTE_ENDERECO_DIRIGENTE: 'Comprovante de Endereço',
  CERTIDAO_TCE_DIRIGENTE: 'Certidão Negativa do TCE/MT - Pessoa Física',
};

export const STATUS_DOCUMENTO_LABELS: Record<StatusDocumento, string> = {
  PENDENTE_ENVIO: 'Em Análise',
  ENVIADO: 'Enviado',
  EM_ANALISE: 'Em Análise',
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
  VENCIDO: 'Vencido',
  SUBSTITUIDO: 'Substituído',
};

export const STATUS_DOCUMENTO_COLORS: Record<StatusDocumento, string> = {
  PENDENTE_ENVIO: 'bg-surface-muted text-fg border-line',
  ENVIADO: 'bg-info-soft text-info-fg border-info-line',
  EM_ANALISE: 'bg-warning-soft text-warning-text border-warning-line',
  APROVADO: 'bg-success-soft text-success-text border-success-line',
  REPROVADO: 'bg-danger-soft text-danger-text border-danger-line',
  VENCIDO: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:border-orange-800 dark:text-orange-300',
  SUBSTITUIDO: 'bg-surface-muted text-fg-muted border-line-strong',
};

export const TIPOS_DECLARACAO: TipoDocumentoInstitucional[] = [
  'DECLARACAO_CAPACIDADE_TECNICA',
  'COMPROVANTE_EXPERIENCIA',
  'DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES',
  'RELACAO_NOMINAL_DIRIGENTES',
  'DECLARACAO_CONDICOES_INSTALACAO',
  'DECLARACAO_NAO_INCORRE_IMPEDIMENTOS',
  'DECLARACAO_NAO_CONTRATACAO',
];

export const DOCUMENTOS_OBRIGATORIOS: TipoDocumentoInstitucional[] = [
  'DECLARACAO_CAPACIDADE_TECNICA',
  'COMPROVANTE_EXPERIENCIA',
  'DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES',
  'RELACAO_NOMINAL_DIRIGENTES',
  'DECLARACAO_CONDICOES_INSTALACAO',
  'DECLARACAO_NAO_INCORRE_IMPEDIMENTOS',
  'DECLARACAO_NAO_CONTRATACAO',
  'CARTAO_CNPJ',
  'ESTATUTO_SOCIAL',
  'ALVARA_FUNCIONAMENTO',
  'ATA_ELEICAO_DIRETORIA',
  'COMPROVANTE_INSCRICAO_CONSELHO',
  'COMPROVANTE_ENDERECO_INSTITUICAO',
  'CERTIDAO_TRIBUTOS_FEDERAIS',
  'CERTIDAO_TRIBUTOS_ESTADUAIS_PGE',
  'CERTIFICADO_FGTS',
  'CERTIDAO_DEBITOS_TRABALHISTAS',
  'CERTIDAO_DEBITOS_MUNICIPAIS',
  'CERTIDAO_TCE_INSTITUICAO',
];

export const DOCUMENTOS_INSTITUICAO: TipoDocumentoInstitucional[] = [
  'DECLARACAO_CAPACIDADE_TECNICA',
  'COMPROVANTE_EXPERIENCIA',
  'DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES',
  'RELACAO_NOMINAL_DIRIGENTES',
  'DECLARACAO_CONDICOES_INSTALACAO',
  'DECLARACAO_NAO_INCORRE_IMPEDIMENTOS',
  'DECLARACAO_NAO_CONTRATACAO',
  'CARTAO_CNPJ',
  'ESTATUTO_SOCIAL',
  'ALVARA_FUNCIONAMENTO',
  'DECLARACAO_UTILIDADE_PUBLICA',
  'ATA_ELEICAO_DIRETORIA',
  'COMPROVANTE_INSCRICAO_CONSELHO',
  'COMPROVANTE_ENDERECO_INSTITUICAO',
  'CERTIDAO_TRIBUTOS_FEDERAIS',
  'CERTIDAO_TRIBUTOS_ESTADUAIS_PGE',
  'CERTIFICADO_FGTS',
  'CERTIDAO_DEBITOS_TRABALHISTAS',
  'CERTIDAO_DEBITOS_MUNICIPAIS',
  'CERTIDAO_TCE_INSTITUICAO',
];

export const FORMATOS_ACEITOS = ['.pdf', '.jpg', '.jpeg', '.png'];
export const TAMANHO_MAXIMO_MB = 10;
export const TAMANHO_MAXIMO_BYTES = TAMANHO_MAXIMO_MB * 1024 * 1024;

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getFileIcon = (filename: string): string => {
  const ext = filename.toLowerCase().split('.').pop();
  if (ext === 'pdf') return '📄';
  if (['jpg', 'jpeg', 'png'].includes(ext || '')) return '🖼️';
  return '📎';
};

export const calcularDiasParaVencimento = (dataValidade: string): number => {
  // Compara só o dia (calendário local): documento que vence hoje ainda é válido (0 dias).
  const [ano, mes, dia] = dataValidade.slice(0, 10).split('-').map(Number);
  const agora = new Date();
  const hoje = Date.UTC(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const vencimento = Date.UTC(ano, mes - 1, dia);
  return Math.round((vencimento - hoje) / (1000 * 60 * 60 * 24));
};

export const verificarDocumentoVencido = (dataValidade?: string): boolean => {
  if (!dataValidade) return false;
  return calcularDiasParaVencimento(dataValidade) < 0;
};

export const verificarDocumentoProximoVencimento = (dataValidade?: string): number | null => {
  if (!dataValidade) return null;
  const dias = calcularDiasParaVencimento(dataValidade);
  if (dias >= 0 && dias <= 30) return dias;
  return null;
};

